/**
 * QuickNoteEntry Component
 *
 * A minimal text entry field for quickly adding journal entries.
 * Uses RichTextEditor with a simplified interface.
 */

import { useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

// ============================================
// TYPES
// ============================================

export interface QuickNoteEntryProps {
  /** Handler when note is saved */
  onSave: (content: string, plainText: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional class names */
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export function QuickNoteEntry({
  onSave,
  placeholder = "What's on your mind?",
  className,
}: QuickNoteEntryProps) {
  const [isFocused, setIsFocused] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand-600 underline hover:text-brand-700',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none',
          'px-4 py-3 min-h-[60px]',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5'
        ),
      },
      handleKeyDown: (_view, event) => {
        // Cmd/Ctrl + Enter to submit
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          handleSave();
          return true;
        }
        return false;
      },
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => {
      // Delay to allow button clicks
      setTimeout(() => {
        if (editor?.isEmpty) {
          setIsFocused(false);
        }
      }, 200);
    },
  });

  // Handle save
  const handleSave = useCallback(() => {
    if (!editor || editor.isEmpty) return;

    const html = editor.getHTML();
    const plainText = editor.getText();

    onSave(html, plainText.slice(0, 200));

    // Clear editor
    editor.commands.clearContent();
    editor.commands.focus();
  }, [editor, onSave]);

  // Check if editor has content
  const hasContent = editor && !editor.isEmpty;

  return (
    <div
      className={cn(
        'rounded-lg border bg-white overflow-hidden transition-all',
        isFocused
          ? 'border-brand-300 ring-2 ring-brand-100 shadow-sm'
          : 'border-gray-200 hover:border-gray-300',
        className
      )}
    >
      <EditorContent
        editor={editor}
        className={cn(
          // Placeholder styling
          '[&_.is-editor-empty:first-child::before]:text-gray-400',
          '[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
          '[&_.is-editor-empty:first-child::before]:float-left',
          '[&_.is-editor-empty:first-child::before]:h-0',
          '[&_.is-editor-empty:first-child::before]:pointer-events-none'
        )}
      />

      {/* Action bar - shown when focused or has content */}
      {(isFocused || hasContent) && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-400">Press Ctrl+Enter to save</span>
          <Button size="sm" onClick={handleSave} disabled={!hasContent} className="h-8">
            <Plus className="h-4 w-4 mr-1" />
            Add Entry
          </Button>
        </div>
      )}
    </div>
  );
}
